import { storeToRefs } from 'pinia';
import { Logger } from '~~/helpers/logger';
import type { CartDiscountKey } from '~~/constants/cartDiscounts';
import type { CartDiscountRaw, DiscountData } from '~~/types/discounts';
import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import { cartDiscountsStore } from '~/stores/cartDiscounts';

/**
 * Cache of commercetools cart discounts fetched by their keys. It is category
 * agnostic - any discount we need to know about before it gets applied to the
 * cart can be loaded through it, and all keys asked for in one `load` call
 * travel in a single request. Telling one discount from another is then up to
 * the composable interpreting it (e.g. `useDistinctProductsDiscount`).
 */
export default () => {

  const { makeCommercetoolsRequest } = useExpondoMiddleware();
  const store = cartDiscountsStore();
  const { cartDiscountsByKey } = storeToRefs(store);

  const getCartDiscount = (discountKey: CartDiscountKey): CartDiscountRaw | null => {

    return cartDiscountsByKey.value[discountKey] || null;

  };

  const load = async (discountKeys: CartDiscountKey[]) => {

    const keysToLoad = store.keysToLoad(discountKeys);

    if (!keysToLoad.length) {

      return;

    }

    store.addPendingKeys(keysToLoad);
    try {

      const res = await makeCommercetoolsRequest<DiscountData>({
        endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.GET_CART_DISCOUNTS_BY_KEYS,
        data: {
          keys: keysToLoad,
        },
      });

      store.setCartDiscounts({
        discountKeys: keysToLoad,
        results: res?.data?.cartDiscounts?.results || [],
      });

    } catch (err) {

      Logger.error('error.useCartDiscountsStore', String(err));

    } finally {

      store.removePendingKeys(keysToLoad);

    }

  };

  return {
    ...store,
    cartDiscountsByKey,
    getCartDiscount,
    load,
  };

};
